import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { IconComponent } from '../../shared/components/icon.component';
import { PublicFooterComponent } from '../../public/public-footer/public-footer.component';
import { GateCountdownComponent } from './gate-countdown.component';
import { PrelaunchFormComponent } from './prelaunch-form.component';

/**
 * Coming-soon announcement for the portal gate.
 */
@Component({
  selector: 'app-prelaunch-landing',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, PrelaunchFormComponent, GateCountdownComponent, PublicFooterComponent],
  styleUrl: './prelaunch-landing.component.css',
  template: `
    <div class="pl">
      <header class="pl-header">
        <div class="pl-shell">
          <a href="#top" class="pl-brand">
            <img src="/images/npr-favicon.png" alt="" width="44" height="44" />
            <span>
              <span class="pl-wordmark">NPR.ng</span>
              <span class="pl-wordmark-sub">News and Publication Registry</span>
            </span>
          </a>
          <p class="pl-badge">
            <svg viewBox="0 0 6 4" width="20" height="14" aria-hidden="true">
              <rect width="2" height="4" fill="#008751" />
              <rect x="2" width="2" height="4" fill="#fff" />
              <rect x="4" width="2" height="4" fill="#008751" />
            </svg>
            Nigeria's No. 1 Platform
          </p>
        </div>
      </header>

      <section id="top" class="pl-hero">
        <div class="pl-shell pl-hero-grid">
          <div class="pl-intro">
            <p class="pl-eyebrow">
              <app-icon name="rocket" class="w-3.5 h-3.5"></app-icon>
              Pre-launch
            </p>
            <h1 class="pl-title">
              @if (headingIsComingSoon()) {
                Coming <em>soon.</em>
              } @else {
                {{ heading() }}
              }
            </h1>
            <p class="pl-body">{{ body() }}</p>
            <p class="pl-tagline">A trusted, transparent, nationwide system built for everyone.</p>
            <a href="#subscribe" class="pl-cta">
              Join the pre-launch list
              <app-icon name="arrow-right" class="w-4 h-4"></app-icon>
            </a>
          </div>

          <figure class="pl-art">
            <img
              src="/images/gate-prelaunch-hero.png"
              alt="Laptop showing a verified NPR.ng publication beside official documents"
              width="960"
              height="720"
            />
          </figure>
        </div>

        <div class="pl-shell">
          <div id="features" class="pl-trust">
            @for (item of features; track item.title) {
              <div class="pl-trust-item">
                <span class="pl-trust-icon">
                  <app-icon [name]="item.icon" class="w-5 h-5"></app-icon>
                </span>
                <span>
                  <strong>{{ item.title }}</strong>
                  <small>{{ item.body }}</small>
                </span>
              </div>
            }
          </div>
        </div>
      </section>

      <section id="subscribe" class="pl-notice-wrap">
        <div class="pl-shell">
          <div class="pl-notice">
            <div class="pl-notice-form">
              <app-prelaunch-form variant="editorial"></app-prelaunch-form>
            </div>
            <div class="pl-notice-clock">
              <p class="pl-script">Stay tuned!</p>
              <p class="pl-clock-lead">
                We're putting the final touches to bring you a better way to publish and verify official
                publications.
              </p>
              @if (endsAt()) {
                <p class="pl-clock-kicker">Launching in</p>
                <app-gate-countdown
                  variant="notice"
                  [endsAt]="endsAt()"
                  (expired)="expired.emit()"
                ></app-gate-countdown>
              }
            </div>
          </div>
        </div>
      </section>

      <section id="audience" class="pl-cast">
        <div class="pl-shell">
          <h2>Built for Everyone Who Publishes</h2>
          <ul>
            @for (audience of audiences; track audience.title) {
              <li>
                <span class="pl-cast-icon">
                  <app-icon [name]="audience.icon" class="w-5 h-5"></app-icon>
                </span>
                <span>{{ audience.title }}</span>
              </li>
            }
          </ul>
        </div>
      </section>

      <app-public-footer tone="forest" [flush]="true" [showAuthLinks]="false" footerId="contact" />
    </div>
  `,
})
export class PrelaunchLandingComponent {
  readonly heading = input('Coming Soon!');
  readonly body = input('');
  readonly endsAt = input<string | null>(null);
  readonly expired = output<void>();

  readonly headingIsComingSoon = computed(() => /^coming soon!?$/i.test(this.heading().trim()));

  readonly features = [
    {
      icon: 'shield-check',
      title: 'Official & Trusted',
      body: 'Backed by standards, built for confidence.',
    },
    {
      icon: 'lock-closed',
      title: 'Secure & Reliable',
      body: 'Your data and publications are safe.',
    },
    {
      icon: 'globe',
      title: 'Accessible Nationwide',
      body: 'Available to everyone, anywhere in Nigeria.',
    },
  ];

  readonly audiences = [
    { icon: 'building-office', title: 'Government Agencies' },
    { icon: 'users', title: 'Businesses & Organizations' },
    { icon: 'book-open', title: 'Publishers & Media Houses' },
    { icon: 'academic-cap', title: 'Academia & Institutions' },
    { icon: 'user', title: 'Individuals & the Public' },
  ];
}
